import * as THREE from "../../libs/three.js/build/three.module.js";
import {createDashedLine} from "./tools/perpendicularCapture.js";

// Stregens ender opdateres PÅ STEDET i geometriens egne buffere (setLinePositions() fra perpendicularCapture opretter nye GPU-buffere
// ved hvert kald og frigiver aldrig de gamle - ved hver musebevægelse ville det både lække og lagge). Linjen er lokal: start =
// [0,0,0], slut = b - a, og a ligger på line.position (store UTM-værdier i en Float32-buffer ville ellers miste præcision).
function setSegmentInPlace(line, a, b){
	const start = line.geometry.attributes.instanceStart.data;
	const p = start.array;
	p[0] = 0; p[1] = 0; p[2] = 0;
	p[3] = b.x - a.x; p[4] = b.y - a.y; p[5] = b.z - a.z;
	start.needsUpdate = true;

	// stiplingen læser afstandene (samme som LineSegments2.computeLineDistances(), men uden at oprette nye buffere)
	const distance = line.geometry.attributes.instanceDistanceStart;
	if(distance){
		distance.data.array[0] = 0;
		distance.data.array[1] = Math.hypot(p[3], p[4], p[5]);
		distance.data.needsUpdate = true;
	}
	line.position.copy(a);
}

// Den dynamiske streg i registrerings- og måleværktøjerne: efter det første punkt følger en stiplet streg cursoren fra det
// sidst placerede punkt, så brugeren ser, hvor næste punkt vil lande (samme dynamik som Mål rørdimension og koter). Ved polygon
// vises også lukkelinjen fra cursoren tilbage til det første punkt.
//
// DigitizationModule ejer én instans: værktøjerne sætter ankeret (setRubberBand) efter hvert placeret punkt og rydder det, når
// forløbet slutter (clearRubberBand); modulet flytter streg-enden til det punkt, cursoren peger på (snap-mål eller pick) ved
// hver musebevægelse - det samme punkt, som den blå cursor-kugle viser, og som et klik placerer.
const RUBBER_BAND_COLOR = 0xffdc00; // samme konstruktions-forhåndsvisning som resten af kodebasen

export class RubberBand extends THREE.Group{
	constructor(){
		super();
		this.name = "digitization-rubber-band";
		this.line = createDashedLine(RUBBER_BAND_COLOR);
		this.closeLine = createDashedLine(RUBBER_BAND_COLOR);
		this.line.raycast = () => {}; // aldrig et raycast-hit (som cursor-kuglen)
		this.closeLine.raycast = () => {};
		// afgrænsningskuglen opdateres ikke, når enderne flyttes på stedet - uden dette kunne stregen forsvinde ved kanten af billedet
		this.line.frustumCulled = false;
		this.closeLine.frustumCulled = false;
		this.add(this.line);
		this.add(this.closeLine);
		this.anchor = null;
		this.closeAnchor = null;
	}

	// anchor: det sidst placerede punkt; closeTo: (valgfri) det punkt, lukkelinjen går til (polygonens første punkt).
	set(anchor, closeTo = null){
		this.anchor = anchor ? anchor.clone() : null;
		this.closeAnchor = anchor && closeTo ? closeTo.clone() : null;
		// vises først ved næste musebevægelse (cursorens position er endnu ikke kendt)
		this.line.visible = false;
		this.closeLine.visible = false;
	}

	clear(){
		this.anchor = null;
		this.closeAnchor = null;
		this.line.visible = false;
		this.closeLine.visible = false;
	}

	get active(){
		return this.anchor !== null;
	}

	// position: det punkt cursoren peger på (eller null = skjul). Nul-lange stykker skjules.
	updateCursor(position){
		if(!this.anchor || !position){
			this.line.visible = false;
			this.closeLine.visible = false;
			return;
		}

		const hasLength = this.anchor.distanceToSquared(position) > 1e-12;
		this.line.visible = hasLength;
		if(hasLength) setSegmentInPlace(this.line, this.anchor, position);

		const showClose = !!this.closeAnchor && position.distanceToSquared(this.closeAnchor) > 1e-12;
		this.closeLine.visible = showClose;
		if(showClose) setSegmentInPlace(this.closeLine, position, this.closeAnchor);
	}

	refreshResolution(width, height){
		if(this.line.visible) this.line.material.resolution.set(width, height);
		if(this.closeLine.visible) this.closeLine.material.resolution.set(width, height);
	}
}
